<script setup>
import { useTemplateRef } from 'vue';
import Button from 'next/button/Button.vue';
import ConditionRow from 'dashboard/components-next/filter/ConditionRow.vue';

// Extracted from ContactsFilter.vue: just the "list of conditions + add/remove
// + validate" behaviour, without the modal/segment chrome — an Audience
// (spec 137) needs this embedded 6 times (contact/company/deal x
// include/exclude) inside one form, not as a standalone full-screen filter.
const props = defineProps({
  filterTypes: { type: Array, required: true },
  addLabel: { type: String, default: '' },
});

const conditions = defineModel({ type: Array, default: () => [] });

const defaultCondition = () => ({
  attributeKey: props.filterTypes[0]?.attributeKey ?? '',
  filterOperator:
    props.filterTypes[0]?.filterOperators?.[0]?.value ?? 'equal_to',
  values: '',
  queryOperator: 'and',
  attributeModel: 'standard',
});

const addCondition = () => {
  conditions.value.push(defaultCondition());
};

// Unlike ContactsFilter.vue, removing the last condition leaves the list
// empty instead of resetting to a blank row — "no criteria for this entity"
// is a valid, meaningful state for an Audience section (see
// Audiences::ResolveContactsService on the backend).
const removeCondition = index => {
  conditions.value.splice(index, 1);
};

const conditionsRef = useTemplateRef('conditionsRef');

const validate = () => {
  if (!conditionsRef.value) return true;
  return conditionsRef.value.every(condition => condition.validate());
};

defineExpose({ validate });
</script>

<template>
  <div class="grid gap-2">
    <ul v-if="conditions.length" class="grid gap-2 my-0 list-none">
      <template v-for="(condition, index) in conditions" :key="index">
        <ConditionRow
          v-if="index === 0"
          ref="conditionsRef"
          v-model:attribute-key="condition.attributeKey"
          v-model:filter-operator="condition.filterOperator"
          v-model:values="condition.values"
          :filter-types="filterTypes"
          aligned
          :show-query-operator="false"
          @remove="removeCondition(index)"
        />
        <ConditionRow
          v-else
          ref="conditionsRef"
          v-model:attribute-key="condition.attributeKey"
          v-model:filter-operator="condition.filterOperator"
          v-model:query-operator="conditions[index - 1].queryOperator"
          v-model:values="condition.values"
          aligned
          show-query-operator
          :filter-types="filterTypes"
          @remove="removeCondition(index)"
        />
      </template>
    </ul>
    <Button
      v-if="addLabel"
      type="button"
      sm
      ghost
      slate
      class="self-start"
      @click="addCondition"
    >
      {{ addLabel }}
    </Button>
  </div>
</template>
